import React, { ReactNode, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import crossIcon from "@/assets/icons/croosIcon.svg";
// edit icon
import { FiEdit, FiTrash2 } from "react-icons/fi";

interface FormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onEditClick?: () => void;
    onDeleteClick?: () => void;
    children: ReactNode;
    width?: string;
    modalTitle: string;
    height?: string;
    overflow?: string;
    customHeader?: any;
    isEditButton?: boolean;
    isDeleteButton?: boolean;
    footer?: ReactNode;
    disableOutsideClick?: boolean;
}

const FormModal: React.FC<FormModalProps> = ({
    modalTitle,
    isOpen,
    onClose,
    width,
    height,
    overflow,
    children,
    customHeader,
    isEditButton = false,
    onEditClick,
    isDeleteButton = false,
    onDeleteClick,
    footer,
    disableOutsideClick = false,
}) => {
    const {
        handleSubmit,
        formState: { isValid },
    } = useForm({ mode: "onChange" });

    // Close the modal when clicking outside of it
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (disableOutsideClick) {
                return;
            }
            const modal = document.getElementById("modal-content");
            const target = event.target as Element;
            if (target && target.closest(".pac-container")) {
                return;
            }
            if (target && target.closest("[data-alert-modal]")) {
                return;
            }
            if (modal && !modal.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.body.classList.add("overflow-y-hidden");
            document.addEventListener("mousedown", handleOutsideClick);
        } else {
            document.removeEventListener("mousedown", handleOutsideClick);

            // Check if no other modals are open before removing the overflow class
            const otherModalsOpen =
                document.querySelectorAll(".modal-is-open").length > (isOpen ? 1 : 0);
            if (!otherModalsOpen) {
                document.body.classList.remove("overflow-y-hidden");
            }
        }

        // Cleanup on unmount
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            const otherModalsOpen =
                document.querySelectorAll(".modal-is-open").length > (isOpen ? 1 : 0);
            if (!otherModalsOpen) {
                document.body.classList.remove("overflow-y-hidden");
            }
        };
    }, [isOpen, onClose, disableOutsideClick]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 modal-is-open">
            <div
                id="modal-content"
                className={`relative max-h-[95vh] bg-white rounded-3xl shadow-lg mx-3 flex flex-col ${width && width
                    } ${overflow && overflow}`}
            >
                <div className="sticky top-0 shrink-0">
                    <div className="flex justify-between bg-p-gray py-4 px-8 rounded-t-2xl">
                        <h1 className="font-semibold text-lg">{modalTitle}</h1>
                        <div className="flex gap-5 items-center">
                            {isEditButton && (
                                <button
                                    disabled={!isValid}
                                    className={`bg-primary-blue flex gap-3 cursor-pointer text-black bg-white px-4 py-2 rounded-full font-medium text-sm  ${!isValid
                                        ? "opacity-50 cursor-not-allowed hover:bg-primary-blue"
                                        : ""
                                        }`}
                                    onClick={handleSubmit(() => {
                                        onEditClick && onEditClick();
                                    })}
                                >
                                    <FiEdit size={18} className="text-gray-600 cursor-pointer" />
                                    Edit
                                </button>
                            )}
                            {isDeleteButton && (
                                <button
                                    className="bg-primary-red flex gap-3 cursor-pointer text-white px-4 py-2 rounded-full font-medium text-sm"
                                    onClick={onDeleteClick}
                                >
                                    <FiTrash2 size={18} className="text-white cursor-pointer" />
                                    Delete
                                </button>
                            )}
                            <button
                                // className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                                onClick={onClose}
                            >
                                <img src={crossIcon} className="w-6" />
                            </button>
                        </div>
                    </div>
                    {customHeader}
                </div>
                <div
                    className="py-4 px-8 overflow-y-auto flex-1"
                    style={{ borderRadius: "inherit" }} // Use borderRadius inherit to apply the same rounded corners
                >
                    {children}
                </div>
                {footer && (
                    <div className="shrink-0 border-t border-gray-100 px-8 py-4 rounded-b-2xl bg-white">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormModal;
